<template>
<div class="facilities-list-container pa-5">
  <!-- Facilities Limit Progress Bar Card -->
  <v-card class="mb-4 elevation-2">
    <v-card-title class="subtitle-1 font-weight-medium">
      <v-icon small left>mdi-office-building</v-icon> Facilities Usage
    </v-card-title>
    <v-card-text class="pb-4">
      <CountProgress
        :count="company.facilitiesLimitUsed || 0"
        :limit="company.facilitiesLimitCount || 0"
        :unlimited="+company.facilitiesLimit === -1"
        :minWidth="300"
        icon="mdi-office-building"
      />
    </v-card-text>
  </v-card>
  
  <!-- Facilities Data Table Card -->
  <v-card class="elevation-2" >
    <v-card-title class="d-flex justify-space-between align-center">
      <span>Facilities</span>
      <v-btn color="primary" small @click="addFacility" 
      :disabled="company.facilitiesLimitUsed>=company.facilitiesLimitCount&&company.facilitiesLimitCount!==-1">
        <v-icon left small>mdi-plus</v-icon>
        Add Facility
      </v-btn>
    </v-card-title>
  <v-card-text style="min-height: 400px;">
    <v-data-table
    class="elevation-0 pa-2 rounded-0 d-flex flex-column table-sticky custom-table  "
    style="min-height: 400px;"
    :headers="viewOnlyFacilityHeaders"
    :items="entries"
    disable-pagination
    :loading="loading"

    hide-default-footer>
    <template v-slot:no-data>
      <div class="text-center d-flex flex-column">
        <img width="300" src="/img/notFound.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">No Data Available!</p>
      </div>
    </template>

    <template v-slot:loading>
      <div class="text-center d-flex flex-column">
        <img width="300" src="/img/loading.svg" class="py-5 my-8" style="opacity: 0.54" />
        <p class="font-weight-medium">Loading Facilities...</p>
      </div>
    </template>
    <!-- Top slot removed as Add Facility button is now in card title -->
    <template v-slot:[`item.actions`]="{ item }">
      <v-menu
        bottom
        right
        offset-y
        attach
        transition="slide-y-transition"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            class="mx-2"
          >
            <v-icon>mdi-dots-vertical</v-icon>
          </v-btn>
        </template>
        <v-list dense  >
          <v-list-item @click="editFacility(item)">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-pencil</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Update</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="confirmDeleteFacility(item)">
            <v-list-item-icon class="mr-2">
              <v-icon small>mdi-delete</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>Delete</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
    
    <template v-slot:[`item.id`]="{ item }">
      <div class="d-inline-flex">
       <v-img :src="item.imageUrl" width="50" height="50" class="rounded"></v-img>
      </div>
    </template>
    <template v-slot:[`item.createdBy`]="{ item }">
      <div class="d-inline-flex">
        <user-avatar :user="item.createdBy" hide-roles disable-open-link></user-avatar>
      </div>
    </template>
    <template #[`item.address`]="{ item }">
      <span class="ml-2">{{
        item.address?.line1 ||
        item.address?.city + ', ' + item.address?.state + ', ' + item.address?.country
      }}</span>
    </template>
    <template #[`item.client`]="{ item }">
      <span class="ml-2">{{ item.client?.name || item.clientName || '-' }}</span>
    </template>
    <template #[`item.smallModelsLimitUsed`]="{ item }">
      <CountProgress
        :count="item.smallModelsLimitUsed"
        :limit="item.smallModelsLimitCount"
        :unlimited="+item.smallModelsLimitCount === -1"
        :minWidth="160"
        icon="mdi-folder-cog-outline"
      />
    </template>
    <template #[`item.scanUploadLimitUsed`]="{ item }">
      <CountProgress
        :count="item.scanUploadLimitUsed"
        :limit="item.scanUploadLimit"
        :unlimited="+item.scanUploadLimit === -1"
        :minWidth="160"
        icon="mdi-upload"
      />
    </template>
    <template #[`item.scanDownloadLimitUsed`]="{ item }">
      <CountProgress
        :count="item.scanDownloadLimitUsed"
        :limit="item.scanDownloadLimit"
        :unlimited="+item.scanDownloadLimit === -1"
        :minWidth="160"
        type="fileSize"
        icon="mdi-download"
      />
    </template>
  </v-data-table>
  </v-card-text>
  </v-card>
  <ManagementEditFacility ref="editFacility" @facility-updated="refreshFacilities"/>
</div>
</template>

<script>
  import UserAvatar from '@Share/components/UserAvatar.vue';
  import viewOnlyFacilityHeaders from '@Facility/config/viewOnlyFacility.headers';
  import companyService from '@/features/Company/service/company-management.service';
  import CountProgress from '@Company/components/CountProgress.vue';
  import ManagementEditFacility from './ManagementEditFacility.vue';
  import { log, warn, error } from '@/utils/debugLogger';

  export default {
    name: 'view-only-facility-list',
    props: {
      company: {
        type: Object,
        default: () => ({}),
      },
      loading: {
        type: Boolean,
        default: false,
      },
    },
    mounted() {
      companyService.getFacilities(this.company.id).then((res) => {
        this.entries = res.data.items ?? [];
      });
    },
    data() {
      return {
        viewOnlyFacilityHeaders,
        entries:[]
      };
    },
    methods: {
      addFacility() {
        this.$refs.editFacility.open(this.company, null);
      },
      editFacility(facility) {
        this.$refs.editFacility.open(this.company, facility.id);
      },
      confirmDeleteFacility(facility) {
        this.$dialog.confirm({
          title: 'Delete Facility',
          text: `Are you sure you want to delete facility "${facility.name}"?`,
          persistent: true,
          actions: {
            false: 'Cancel',
            true: {
              text: 'Delete',
              color: 'error'
            }
          }
        }).then(confirmed => {
          if (confirmed) {
            this.deleteFacility(facility);
          }
        });
      },
      deleteFacility(facility) {
        const managementFacilitiesService = require('@/features/Company/service/management-facilities.service.js').default;
        
        this.$set(this, 'loading', true);
        managementFacilitiesService.delete(this.company.id, facility.id)
          .then(() => {
            this.$dialog.notify.success(`Facility "${facility.name}" deleted successfully`, {
              position: 'top-right',
              timeout: 3000
            });
            this.refreshFacilities();
          })
          .catch(err => {
            error('ViewOnlyFacilityList', 'Failed to delete facility', err);
            this.$dialog.notify.error(`Failed to delete facility: ${err.message || 'Unknown error'}`, {
              position: 'top-right',
              timeout: 5000
            });
          })
          .finally(() => {
            this.$set(this, 'loading', false);
          });
      },
      refreshFacilities() {
        this.$set(this, 'loading', true);
        companyService.getFacilities(this.company.id)
          .then((res) => {
            this.entries = res.data.items ?? [];
          })
          .catch(err => {
            error('ViewOnlyFacilityList', 'Failed to refresh facilities', err);
          })
          .finally(() => {
            this.$set(this, 'loading', false);
          });
      },
    },
    components: {
      UserAvatar,
      CountProgress,
      ManagementEditFacility,
    },
  };
</script>
<style scoped>
.facilities-list-container {
  min-height: 500px;
}
</style>